import { useMutation, useQuery } from '@apollo/client';
import {
    faArrowsRepeat,
    faArrowUpFromBracket,
    faLink,
    faFolder,
    faLock,
    faPen,
    faPlus,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { format, isSameDay, isToday } from 'date-fns';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';

import Alert from '../../core/components/Alert.tsx';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Layout from '../../core/components/Layout.tsx';
import Loader from '../../core/components/Loader.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../core/components/Navigation/internal/queries.ts';
import Page from '../../core/components/Page';
import { useToaster } from '../../core/components/Toast.tsx';
import { ThinkingDots } from '../../core/components/worki/Accessory.tsx';
import { Pose, SIT, THINKING } from '../../core/components/worki/Pose.tsx';
import Worki from '../../core/components/worki/Worki.tsx';
import WorkiThinking from '../../core/components/worki/WorkiThinking.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { useEscapeKey } from '../../core/utils/hooks/useEscapeKey.ts';
import { useLocalStorage } from '../../core/utils/hooks/useLocalStorage.ts';
import useMediaQuery, { MOBILE_MEDIA_QUERY } from '../../core/utils/hooks/useMediaQuery.ts';
import { skipPollAttemptWhenHidden, useRefetchOnTabVisible } from '../../core/utils/hooks/useRefetchOnTabVisible.ts';
import { url } from '../../core/utils/link.ts';
import { canManageAgents, canSeeDocuments, canUseChat } from '../../users/utils.ts';
import AutomationDialog, { AutomationDialogMode } from '../components/automations/AutomationDialog.tsx';
import { AutomationsPage } from '../components/automations/AutomationsPage.tsx';
import { formFromToolArgs, ScheduleFormState } from '../components/automations/scheduleFormat.ts';
import { ScheduleHeaderButton } from '../components/automations/ScheduleHeaderButton.tsx';
import { EDIT_SCHEDULE_STATE, useScheduleRunToasts } from '../components/automations/useScheduleRunToasts.ts';
import { AttachmentChip, AttachmentPreviewModal } from '../components/ChatAttachment.tsx';
import { Composer, ComposerHandle } from '../components/Composer.tsx';
import { ConversationSidebar } from '../components/ConversationSidebar.tsx';
import { MessageBubble } from '../components/MessageBubble.tsx';
import { extractScheduleOffer, extractSuggestions, hasDetailedOptions } from '../components/messageContent.ts';
import { ProjectAgentBadge } from '../components/ProjectAgentBadge.tsx';
import { displayName } from '../components/ProjectSharingFields.tsx';
import { ProjectsPage } from '../components/ProjectsPage.tsx';
import { ScheduleCreatedMarker, ScheduledRunMarker, ScheduleRunningMarker } from '../components/ScheduledRunMarker.tsx';
import { ShareAccessPopover } from '../components/ShareAccessPopover.tsx';
import { ShareConversationModal, shareRecipientsLabel } from '../components/ShareConversationModal.tsx';
import { SidebarToggle } from '../components/SidebarToggle.tsx';
import {
    ChatErrorNotice,
    ChatFullNotice,
    CostLimitNotice,
    OfflineNotice,
    ToolFailureNotice,
} from '../components/ThreadNotices.tsx';
import { extractCreatedSchedules, partitionToolCalls } from '../components/toolDisplay.tsx';
import { formatTimestamp } from '../components/toolDisplay.tsx';
import { isNoDataResult } from '../components/toolResultData.ts';
import { LongChatWarning, MonthlyBudgetWarning } from '../components/UsageWarnings.tsx';
import WorkiTeaserPage from '../components/WorkiTeaserPage.tsx';
import {
    CHANNEL_FIELDS,
    CHAT_ENTRY_POINT,
    CHAT_PATH,
    CONVERSATION_PATH,
    PROJECT_PATH,
    PROJECTS_PATH,
    PROMPT_LIBRARY_PATH,
    AUTOMATION_PATH,
    AUTOMATIONS_PATH,
    SHARED_CONVERSATIONS_UPDATE_INTERVAL,
} from '../constants.ts';
import { graphqlErrorMessage } from '../errors.ts';
import { useChatUsage } from '../hooks/useChatUsage.ts';
import { useConversationStream } from '../hooks/useConversationStream.ts';
import { useHasNoEnabledAgent } from '../hooks/useHasNoEnabledAgent.ts';
import { useOnlineStatus } from '../hooks/useOnlineStatus.ts';
import {
    PromptCategoryKey,
    PromptCategoryTranslations,
    resolveFeaturedPrompts,
    resolveSingleDocumentType,
} from '../promptLibraryConfig.ts';
import {
    CONFIRM_AGENT_TOOL_CALL,
    CREATE_CONVERSATION,
    DELETE_CONVERSATION,
    GET_AVAILABLE_CHAT_MODELS,
    GET_CHAT_AGENTS,
    GET_CHAT_TOOLS,
    GET_CONVERSATION,
    GET_PROJECTS,
    GET_CONVERSATION_SCHEDULES,
    GET_MY_TRIGGERS,
    CONTINUE_SHARED_CONVERSATION,
    SET_SHARED_SCHEDULE_MUTED,
    GET_CONVERSATIONS,
    GET_SHARED_CONVERSATIONS,
    UPLOAD_CONVERSATION_ATTACHMENT,
} from '../queries.ts';
import { renderPromptLibraryText, promptRequiresInput, promptTemplateStillMatches } from '../utils/promptLibrary.ts';
import { formatRelativeDay, formatTime } from '../utils/relativeDate.ts';

const LockIcon = withIcon(faLock);
const MoreIcon = withIcon(faPlus);

const TrashIcon = withIcon(faTrash);
const ProjectIcon = withIcon(faFolder);
const OriginIcon = withIcon(faLink);
const RepeatIcon = withIcon(faArrowsRepeat);
const SharedIcon = withIcon(faArrowUpFromBracket);
const EditIcon = withIcon(faPen);

const Footnote = () => {
    const { t } = useTranslation('chat');
    return (
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-1 px-6 pb-3 pt-1 text-center text-xs leading-relaxed text-balance text-content-default">
            {/* the icon is inline so it wraps with the first line instead of floating beside the centred text block */}
            <span>{t('footnote')}</span>
            <span>{t('footnoteAi')}</span>
        </div>
    );
};

const ModelBadge = ({ modelLabel }) => (
    <div className="hidden md:flex items-center gap-1.5 rounded-lg border border-solid border-line-strong px-2.5 py-1.5 text-xs font-medium text-content-medium">
        <LockIcon className="text-content-brand-strong" />
        {modelLabel}
    </div>
);

// the project a chat runs in (ENG-11385): an inbox agent's project shows the agent icon, a shared one the folder
const ProjectBadge = ({ project }: { project: { name: string; agent?: any } }) => (
    <div className="hidden md:flex items-center gap-1.5 rounded-lg border border-solid border-line-strong px-2.5 py-1.5 text-xs font-medium text-content-medium">
        <ProjectIcon className="text-content-medium" />
        {project.name}
        <ProjectAgentBadge project={project} iconOnly className="text-content-weak" />
    </div>
);

// Worki at work in the thread (a turn being answered, or a scheduled run in progress): three bouncing dots while it
// thinks, hopping dots once it works on tool calls (and for the rest of the turn), a short fade-out when it is done
const ThreadThinkingIndicator = ({ active, working, label }: { active: boolean; working: boolean; label: string }) => {
    const [visible, setVisible] = useState(active);
    const [hasWorked, setHasWorked] = useState(active && working);
    const [shownLabel, setShownLabel] = useState(label);
    const [wasActive, setWasActive] = useState(active);
    if (active !== wasActive) {
        setWasActive(active);
        if (active) {
            setVisible(true);
            setHasWorked(working);
        }
    }
    if (active && working && !hasWorked) setHasWorked(true);
    if (active && label !== shownLabel) setShownLabel(label);

    if (!visible) return null;
    return (
        <WorkiThinking
            className="mb-5"
            state={!active ? 'done' : hasWorked ? 'working' : 'thinking'}
            label={shownLabel}
            onDone={() => setVisible(false)}
        />
    );
};

const SharedNote = ({ share }) => {
    const { t, i18n } = useTranslation('chat');
    const names = [
        ...(share.team ? [t('share.teamLabel', { team: share.team.name })] : []),
        ...(share.members || []).map(displayName),
    ];
    const recipients = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(names);
    const when = isToday(new Date(share.createdAt))
        ? formatTime(share.createdAt)
        : formatRelativeDay(share.createdAt, t);
    return (
        <div className="mb-5 flex items-center justify-center gap-2 text-center text-xs text-content-weak">
            <SharedIcon className="flex-none" />
            <span>
                {t(share.sourceMessage ? 'share.sharedTaskNote' : 'share.sharedChatNote', { recipients, when })}
            </span>
        </div>
    );
};

const DayDivider = ({ date }) => {
    const { t } = useTranslation('chat');
    const value = new Date(date);
    return (
        <div className="my-4 text-center text-xs font-medium text-content-weak">
            {isToday(value) ? t('thread.today') : format(value, 'EEEE, MMM d')}
        </div>
    );
};

const ThreadView = ({
    conversationId,
    schedule = null,
    modelLabels,
    onSchedule,
    onAdjustSchedule = null,
    onEditSchedule,
    onOpenSidebar,
}) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const location = useLocation();
    const { publishToast } = useToaster();
    const threadRef = useRef<HTMLDivElement>(null);
    const initialSendRef = useRef(false);

    const { data, loading, refetch } = useQuery(GET_CONVERSATION, {
        variables: { id: conversationId },
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('thread.loadError')), status: 'error' }),
    });
    const [deleteConversation] = useMutation(DELETE_CONVERSATION, {
        // schedules cascade with the conversation, so refresh them too
        refetchQueries: [{ query: GET_CONVERSATIONS }, { query: GET_CONVERSATION_SCHEDULES }],
    });
    // sharing (ENG-11386): a recipient continues a shared snapshot as a chat of their own
    const [continueShared, { loading: continuing }] = useMutation(CONTINUE_SHARED_CONVERSATION, {
        refetchQueries: [{ query: GET_CONVERSATIONS }],
    });
    const [shareTarget, setShareTarget] = useState<{ message: any | null } | null>(null);
    // a chat that receives a schedule's runs (ENG-11387): its owner mutes the schedule from the origin strip
    const [setSharedScheduleMuted, { loading: muting }] = useMutation(SET_SHARED_SCHEDULE_MUTED);
    const handleMuteDelivery = async (scheduleId: string, muted: boolean) => {
        try {
            await setSharedScheduleMuted({ variables: { id: scheduleId, muted } });
        } catch (error) {
            publishToast({ description: graphqlErrorMessage(error, t('share.muteError')), status: 'error' });
        }
    };
    const {
        isStreaming,
        isStopping,
        toolStatus,
        streamingMessage,
        chatError,
        unsentMessage,
        failedMessage,
        clearFailure,
        isSlow,
        truncated,
        budgetExceeded,
        setBudgetExceeded,
        stopStreaming,
        sendMessage: streamSend,
        editAndResend: streamEdit,
        retryLastMessage,
    } = useConversationStream(conversationId);
    const { limitReached: costLimitReached } = useChatUsage();
    const online = useOnlineStatus();
    const composerRef = useRef<ComposerHandle>(null);

    const conversation = data?.agentConversation;
    const [pendingMessage, setPendingMessage] = useState<string | null>(null);
    // while an edit replays, the edited message and everything after it are hidden immediately for clear feedback
    const [pendingEditId, setPendingEditId] = useState<string | null>(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    // "Not now" on the schedule-offer banner hides it for this message without sending anything
    const [dismissedScheduleOffer, setDismissedScheduleOffer] = useState<string | null>(null);

    useEffect(() => {
        threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
    }, [conversation?.messages?.length, pendingMessage]);

    const handleSend = async (message: string, attachmentIds: string[] = []) => {
        // guarded here (not just at each call site — Composer disabled state, featured-prompt
        // buttons) so a caller that bypasses the UI-level disabling, like the router-state
        // handoff from /chat/prompts, can't send once the monthly budget is exhausted
        if (costLimitReached) return;
        setBudgetExceeded(false);
        setPendingMessage(message);
        await streamSend(message, attachmentIds);
        setPendingMessage(null);
    };

    const handleEdit = async (messageId: string, message: string) => {
        setBudgetExceeded(false);
        // mirror the normal send: drop the edited message + everything after right away and show it as pending
        setPendingEditId(messageId);
        setPendingMessage(message);
        await streamEdit(messageId, message);
        setPendingEditId(null);
        setPendingMessage(null);
    };

    // the first message of a brand-new conversation is handed off from NewConversationView via router
    // state; send it here so it streams like every other turn instead of a separate one-off mutation
    useEffect(() => {
        const initialMessage = location.state?.pendingMessage;
        if (!initialMessage || initialSendRef.current) return;
        initialSendRef.current = true;
        const attachmentIds = location.state?.pendingAttachmentIds ?? [];
        // clear the pending state immediately so a refresh doesn't resend the same message
        navigate('.', { replace: true, state: {} });
        handleSend(initialMessage, attachmentIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // a scheduled run writes its messages when it finishes: reload the thread once the polled schedule reports it
    const lastRunAt = schedule?.lastRunAt ?? null;
    const seenLastRunAtRef = useRef(lastRunAt);
    useEffect(() => {
        if (!lastRunAt || lastRunAt === seenLastRunAtRef.current) return;
        seenLastRunAtRef.current = lastRunAt;
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastRunAt]);

    const editFailedMessage = () => {
        composerRef.current?.fillPrompt(failedMessage ?? '');
        clearFailure();
    };

    const handleRetry = async () => {
        setPendingMessage(unsentMessage);
        await retryLastMessage();
        setPendingMessage(null);
    };

    const handleDelete = async () => {
        try {
            await deleteConversation({ variables: { id: conversationId } });
            navigate(url(CHAT_PATH));
        } catch (e) {
            console.error(e);
            publishToast({ description: graphqlErrorMessage(e, t('thread.deleteError')), status: 'error' });
        }
    };

    if (loading && !conversation) return <Loader />;
    if (!conversation) return <div className="p-8 text-sm text-content-medium">{t('thread.notFound')}</div>;

    // a shared snapshot is read-only for everyone; a chat is read-only for anyone but its owner (ENG-11386)
    const isSnapshot = Boolean(conversation.share);
    const readOnly = isSnapshot || !conversation.isOwner;
    const sharerName = conversation.share?.sharedBy ? displayName(conversation.share.sharedBy) : t('projects.admin');
    // a live chat that is not the viewer's own: the support/internal debugging view
    const creatorName = conversation.createdBy ? displayName(conversation.createdBy) : t('projects.admin');
    const handleContinue = async (message = null) => {
        try {
            const { data } = await continueShared({
                variables: { id: conversationId, messageId: message?.id ?? null },
            });
            navigate(url(CONVERSATION_PATH, { conversationId: data.continueSharedAgentConversation.conversation.id }));
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('share.error')), status: 'error' });
        }
    };

    const busy = isStreaming;
    // the chat's schedule in the thread ("4.2 Schedule in chat · states"); a snapshot shows none
    const threadSchedule = isSnapshot ? null : schedule;
    // when editing, hide the edited message and everything after it while the new turn replays
    const editIndex = pendingEditId ? conversation.messages.findIndex((message) => message.id === pendingEditId) : -1;
    const visibleMessages = editIndex >= 0 ? conversation.messages.slice(0, editIndex) : conversation.messages;

    const lastMessage = conversation.messages[conversation.messages.length - 1];

    const isScheduledPrompt = (message) =>
        message.role === 'user' &&
        Boolean(message.scheduleName) &&
        (!message.schedule?.id || message.schedule.id === threadSchedule?.id);
    // a schedule created from a chat proposal is already shown as a created-schedule card on its
    // message; the thread marker is only for schedules created elsewhere (Automations page, dialog)
    const scheduleCreatedAsCard =
        Boolean(threadSchedule) &&
        visibleMessages.some((message) =>
            extractCreatedSchedules(message.toolCalls).some((created) => created.schedule_id === threadSchedule.id)
        );
    // the schedule was created here: its marker goes before the first message after that (or ends the thread)
    const createdMarkerIndex =
        threadSchedule?.createdAt && !scheduleCreatedAsCard
            ? visibleMessages.findIndex(
                  (message) => message.createdAt && new Date(message.createdAt) >= new Date(threadSchedule.createdAt)
              )
            : null;
    // the failed run that auto-paused the schedule: the latest scheduled (not test) run in the thread
    let autoPausedRunId: string | null = null;
    if (threadSchedule?.autoPaused) {
        for (let i = visibleMessages.length - 2; i >= 0; i--) {
            const reply = visibleMessages[i + 1];
            if (!isScheduledPrompt(visibleMessages[i]) || reply.role !== 'assistant' || reply.scheduleTestRun) continue;
            if (reply.scheduleRunStatus === 'failed') autoPausedRunId = visibleMessages[i].id;
            break;
        }
    }
    // a run in progress has no messages yet (they're written when it finishes): its marker and Worki thinking end
    // the thread, unless the thread already holds that run
    const runningSince =
        threadSchedule?.lastRunStatus === 'running' && threadSchedule.lastRunStartedAt && !busy
            ? threadSchedule.lastRunStartedAt
            : null;
    const showRunningRun =
        Boolean(runningSince) &&
        !visibleMessages.some(
            (message) =>
                isScheduledPrompt(message) && message.createdAt && new Date(message.createdAt) >= new Date(runningSince)
        );
    const suggestions =
        !readOnly && !busy && !streamingMessage && !costLimitReached && lastMessage?.role === 'assistant'
            ? extractSuggestions(lastMessage.content)
            : [];
    const scheduleOffer =
        !readOnly &&
        !busy &&
        !streamingMessage &&
        !costLimitReached &&
        lastMessage?.role === 'assistant' &&
        lastMessage.id !== dismissedScheduleOffer
            ? extractScheduleOffer(lastMessage.content)
            : null;
    // the conversation's thumbs up / down sit under Worki's latest answer, once the turn is over
    const latestAnswerId =
        !readOnly && !busy && !streamingMessage && !pendingMessage
            ? ([...visibleMessages].reverse().find((message) => message.role === 'assistant')?.id ?? null)
            : null;
    // the prompt a reply answers, and the reply to a prompt: for retry and share in the action rows
    const promptOf = (index: number) => {
        const prompt = visibleMessages[index - 1];
        return prompt?.role === 'user' && !prompt.scheduleName ? prompt : null;
    };
    const latestPrompt = latestAnswerId === lastMessage?.id ? promptOf(visibleMessages.length - 1) : null;
    const replyOf = (index: number) =>
        visibleMessages[index + 1]?.role === 'assistant' ? visibleMessages[index + 1] : null;
    // where the owner shared the chat: each share's note follows the last message it included
    const sharesAfter = new Map<number, any[]>();
    if (conversation.isOwner && !isSnapshot) {
        (conversation.shares || []).forEach((share) => {
            let index = -1;
            visibleMessages.forEach((message, i) => {
                if (!message.createdAt || new Date(message.createdAt) <= new Date(share.createdAt)) index = i;
            });
            sharesAfter.set(index, [...(sharesAfter.get(index) || []), share]);
        });
    }
    const workiBusy = busy || showRunningRun;
    // a brand-new chat can load the just-persisted user message before the optimistic bubble is cleared;
    // suppress the bubble when that message is already the last one in the thread to avoid a duplicate
    const lastVisible = visibleMessages[visibleMessages.length - 1];
    const fromAutomation = (location.state as { fromAutomation?: { scheduleId: string; name: string } } | null)
        ?.fromAutomation;

    const pendingAlreadyInThread =
        !!pendingMessage && lastVisible?.role === 'user' && lastVisible?.content === pendingMessage;

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex h-14 flex-none items-center gap-3 border-0 border-b border-solid border-line-weak bg-surface-weak px-5">
                <Page.HeaderNavigationToggle />
                {fromAutomation && (
                    <Page.HeaderBreadcrumb
                        to={url(AUTOMATION_PATH, { scheduleId: fromAutomation.scheduleId })}
                        className="max-w-[40%]"
                    >
                        {fromAutomation.name}
                    </Page.HeaderBreadcrumb>
                )}
                <div className="min-w-0 flex-1">
                    <Page.HeaderTitle className="truncate">{conversation.title}</Page.HeaderTitle>
                    <div className="truncate text-xs text-content-weak">
                        Worki
                        {!conversation.toolsEnabled && ` · ${t('thread.chatOnly')}`}
                    </div>
                </div>
                <div className="ml-auto flex flex-none items-center gap-3">
                    <ModelBadge modelLabel={modelLabels[conversation.modelKey] || conversation.modelKey} />
                    {conversation.project && <ProjectBadge project={conversation.project} />}
                    {schedule && !isSnapshot && (
                        <ScheduleHeaderButton schedule={schedule} onClick={() => onEditSchedule(schedule)} />
                    )}
                    {conversation.isOwner && !isSnapshot && (
                        <>
                            <ShareAccessPopover
                                conversation={conversation}
                                onAdd={() => setShareTarget({ message: null })}
                            />
                            <Button
                                variant="ghost"
                                className="px-2"
                                onClick={handleDelete}
                                aria-label={t('thread.delete')}
                            >
                                <TrashIcon />
                            </Button>
                        </>
                    )}
                    <SidebarToggle onClick={onOpenSidebar} />
                </div>
            </div>
            <div ref={threadRef} className="flex-1 overflow-y-auto">
                <div className="mx-auto max-w-3xl px-6 pb-16 pt-6">
                    {isSnapshot && (
                        <div className="mb-6 flex flex-col gap-3">
                            <Alert severity="info">
                                <span>
                                    <span className="font-medium">
                                        {conversation.isOwner
                                            ? t('share.ownSnapshot', {
                                                  recipients: shareRecipientsLabel(conversation.share, t),
                                              })
                                            : t('share.sharedBy', { name: sharerName })}
                                    </span>{' '}
                                    {t('share.snapshotInfo', { date: formatTimestamp(conversation.share.createdAt) })}
                                </span>
                            </Alert>
                            {conversation.share.note && (
                                <div className="rounded-lg border border-solid border-line-weak bg-surface-weak px-4 py-3 text-sm text-content-strong">
                                    <div className="mb-1 text-xs font-medium text-content-medium">
                                        {t('share.noteFrom', { name: sharerName })}
                                    </div>
                                    <div className="whitespace-pre-wrap">{conversation.share.note}</div>
                                </div>
                            )}
                        </div>
                    )}
                    {conversation.continuedFrom && (
                        <div className="mb-6 flex items-center gap-2 text-xs text-content-medium">
                            <OriginIcon className="flex-none" />
                            <span>
                                {t('share.continuedFrom', {
                                    name: conversation.continuedFrom.createdBy
                                        ? displayName(conversation.continuedFrom.createdBy)
                                        : t('projects.admin'),
                                })}{' '}
                                <Link
                                    className="font-medium text-content-brand-default hover:underline"
                                    to={url(CONVERSATION_PATH, { conversationId: conversation.continuedFrom.id })}
                                >
                                    {conversation.continuedFrom.title || t('sidebar.sharedUntitled')}
                                </Link>
                            </span>
                        </div>
                    )}
                    {conversation.scheduledDelivery && (
                        <div className="mb-6 flex items-center gap-2 text-xs text-content-medium">
                            <OriginIcon className="flex-none" />
                            <span>
                                {t('share.scheduledDelivery', {
                                    name: conversation.scheduledDelivery.sender
                                        ? displayName(conversation.scheduledDelivery.sender)
                                        : t('projects.admin'),
                                    schedule: conversation.scheduledDelivery.name,
                                })}
                            </span>
                            {conversation.scheduledDelivery.isMuted && (
                                <span className="font-medium">{t('share.deliveryMuted')}</span>
                            )}
                            <button
                                type="button"
                                className="cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-content-brand-default hover:underline"
                                disabled={muting}
                                onClick={() =>
                                    handleMuteDelivery(
                                        conversation.scheduledDelivery.id,
                                        !conversation.scheduledDelivery.isMuted
                                    )
                                }
                            >
                                {t(
                                    conversation.scheduledDelivery.isMuted
                                        ? 'share.unmuteDelivery'
                                        : 'share.muteDelivery'
                                )}
                            </button>
                        </div>
                    )}
                    {(sharesAfter.get(-1) || []).map((share) => (
                        <SharedNote key={share.id} share={share} />
                    ))}
                    {visibleMessages.map((message, index) => {
                        const isRevealing = streamingMessage?.id === message?.id;
                        const isLastMessage = message.id === lastMessage?.id;
                        const previous = visibleMessages[index - 1];
                        const startsNewDay =
                            message.createdAt &&
                            (!previous?.createdAt ||
                                !isSameDay(new Date(message.createdAt), new Date(previous.createdAt)));
                        // a scheduled run renders as a divider marker in place of its prompt bubble (ENG-10767)
                        const isScheduledRunPrompt = Boolean(message.scheduleName) && message.role === 'user';
                        return (
                            <React.Fragment key={message.id}>
                                {index === createdMarkerIndex && <ScheduleCreatedMarker schedule={threadSchedule} />}
                                {startsNewDay && <DayDivider date={message.createdAt} />}
                                {isScheduledRunPrompt ? (
                                    <ScheduledRunMarker
                                        message={message}
                                        reply={
                                            visibleMessages[index + 1]?.role === 'assistant'
                                                ? visibleMessages[index + 1]
                                                : null
                                        }
                                        autoPaused={message.id === autoPausedRunId}
                                    />
                                ) : (
                                    <MessageBubble
                                        message={isRevealing ? streamingMessage : message}
                                        conversationId={readOnly ? null : conversation.id}
                                        conversationAttachments={conversation.attachments || []}
                                        onAction={readOnly ? null : handleSend}
                                        onEdit={readOnly ? null : handleEdit}
                                        onSchedule={readOnly ? null : onSchedule}
                                        onShare={
                                            !conversation.isOwner || isSnapshot
                                                ? null
                                                : message.role === 'user'
                                                  ? // a prompt shares the exchange it starts, once it is answered
                                                    replyOf(index) &&
                                                    (() => setShareTarget({ message: replyOf(index) }))
                                                  : (target) => setShareTarget({ message: target })
                                        }
                                        onRegenerate={
                                            !readOnly && message.role === 'assistant' && promptOf(index)
                                                ? () => handleEdit(promptOf(index).id, promptOf(index).content)
                                                : null
                                        }
                                        feedback={
                                            message.id === latestAnswerId
                                                ? {
                                                      conversationId,
                                                      userRatingHelpful: conversation.userRatingHelpful ?? null,
                                                  }
                                                : null
                                        }
                                        onContinueFrom={isSnapshot && !conversation.isOwner ? handleContinue : null}
                                        onPreviewAttachment={setPreviewAttachment}
                                        onRetry={
                                            isLastMessage && message.status === 'CANCELLED'
                                                ? retryLastMessage
                                                : undefined
                                        }
                                        actionsDisabled={
                                            readOnly || busy || budgetExceeded || costLimitReached || isRevealing
                                        }
                                        isStreaming={isRevealing}
                                        onAdjustSchedule={readOnly ? null : onAdjustSchedule}
                                        optionsActive={message.id === latestAnswerId && !costLimitReached}
                                        chosenOption={
                                            visibleMessages[index + 1]?.role === 'user'
                                                ? visibleMessages[index + 1].content
                                                : null
                                        }
                                    />
                                )}
                                {(sharesAfter.get(index) || []).map((share) => (
                                    <SharedNote key={share.id} share={share} />
                                ))}
                            </React.Fragment>
                        );
                    })}
                    {createdMarkerIndex === -1 && <ScheduleCreatedMarker schedule={threadSchedule} />}
                    {showRunningRun && <ScheduleRunningMarker startedAt={runningSince} />}
                    <ThreadThinkingIndicator active={showRunningRun} working label={t('thread.thinking')} />
                    {pendingMessage && !pendingAlreadyInThread && (
                        <MessageBubble message={{ role: 'user', content: pendingMessage }} />
                    )}
                    {!pendingMessage && unsentMessage && (
                        <MessageBubble message={{ role: 'user', content: unsentMessage }} unsent />
                    )}
                    <ThreadThinkingIndicator
                        active={busy}
                        working={Boolean(toolStatus?.isWorking)}
                        label={toolStatus?.text ?? t('thread.thinking')}
                    />
                    {busy && isSlow && <div className="mb-5 text-xs text-content-medium">{t('thread.slow')}</div>}
                    {truncated && !streamingMessage && (
                        <div className="mb-5 text-xs text-content-weak">{t('thread.truncatedNotice')}</div>
                    )}
                    {latestPrompt && (
                        <ToolFailureNotice
                            toolCalls={lastMessage.toolCalls}
                            onRetry={() => handleEdit(latestPrompt.id, latestPrompt.content)}
                        />
                    )}
                    {latestPrompt && partitionToolCalls(lastMessage.toolCalls).trail.some(isNoDataResult) && (
                        <div className="mb-5">
                            <Button onClick={() => composerRef.current?.fillPrompt(latestPrompt.content)}>
                                <EditIcon className="mr-2" />
                                {t('thread.editQuestion')}
                            </Button>
                        </div>
                    )}
                    {chatError && (
                        <ChatErrorNotice
                            kind={chatError}
                            onRetry={handleRetry}
                            onEdit={unsentMessage || chatError === 'contentFilter' ? editFailedMessage : null}
                        />
                    )}
                    {scheduleOffer && (
                        <div className="mb-5 flex w-full flex-col gap-2.5 rounded-xl border border-dashed border-line-brand-default bg-surface-brand-weak px-4 py-3">
                            <div className="flex items-start gap-2.5 text-sm text-content-brand-strong">
                                <RepeatIcon className="mt-0.5 flex-none" />
                                {scheduleOffer.question}
                            </div>
                            <div className="flex items-center gap-1">
                                <Button variant="primary" onClick={() => handleSend(scheduleOffer.accept)}>
                                    {scheduleOffer.accept}
                                </Button>
                                <Button variant="ghost" onClick={() => setDismissedScheduleOffer(lastMessage.id)}>
                                    {scheduleOffer.decline || t('thread.scheduleOfferNotNow')}
                                </Button>
                            </div>
                        </div>
                    )}
                    {suggestions.length > 0 && !hasDetailedOptions(suggestions) && !scheduleOffer && (
                        // follow-up suggestions are secondary actions ("4.3 Answer actions")
                        <div className="mb-5 flex flex-wrap gap-2">
                            {suggestions.map((suggestion) => (
                                <Button key={suggestion} variant="outline" onClick={() => handleSend(suggestion)}>
                                    {suggestion}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {(conversation.attachments || []).length > 0 && (
                <div className="mx-auto w-full max-w-3xl px-6 pt-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-content-weak">
                            {t('thread.files')}
                        </span>
                        {conversation.attachments.map((attachment) => (
                            <AttachmentChip
                                key={attachment.id}
                                attachment={attachment}
                                onClick={() => setPreviewAttachment(attachment)}
                                className="border border-solid border-line-strong bg-surface-weak text-content-medium"
                            />
                        ))}
                    </div>
                </div>
            )}
            <AttachmentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
            {shareTarget && (
                <ShareConversationModal
                    conversation={conversation}
                    message={shareTarget.message}
                    onClose={() => setShareTarget(null)}
                />
            )}
            {!readOnly && !budgetExceeded && <LongChatWarning conversationId={conversationId} className="pt-1" />}
            <MonthlyBudgetWarning className="pt-1" />
            {budgetExceeded && (
                <div className="mx-auto w-full max-w-3xl px-6 pt-1">
                    <ChatFullNotice question={unsentMessage ?? failedMessage} />
                </div>
            )}
            {costLimitReached && (
                <div className="mx-auto w-full max-w-3xl px-6 pt-1">
                    <CostLimitNotice />
                </div>
            )}
            {!readOnly && !online && (
                <div className="mx-auto w-full max-w-3xl px-6 pt-1">
                    <OfflineNotice />
                </div>
            )}
            {readOnly ? (
                <div className="mx-auto mb-4 flex w-full max-w-3xl items-center justify-between gap-3 rounded-lg border border-solid border-line-weak bg-surface-weak px-4 py-3">
                    <span className="text-sm text-content-medium">
                        {conversation.isOwner
                            ? t('share.ownSnapshotHint')
                            : isSnapshot
                              ? t('share.readOnlyHint', { name: sharerName })
                              : t('share.supportViewHint', { name: creatorName })}
                    </span>
                    {!conversation.isOwner && isSnapshot && (
                        <Button
                            variant="primary"
                            className="flex-none"
                            onClick={() => handleContinue()}
                            loading={continuing}
                        >
                            {t('share.continue')}
                        </Button>
                    )}
                </div>
            ) : (
                <Composer
                    ref={composerRef}
                    onSend={handleSend}
                    disabled={budgetExceeded || costLimitReached}
                    sendDisabled={!online}
                    placeholder={t('composer.placeholder')}
                    conversationId={conversationId}
                    isStreaming={isStreaming}
                    isStopping={isStopping}
                    onStop={stopStreaming}
                    decoration={<ComposerWorki isThinking={workiBusy} />}
                />
            )}
            <Footnote />
        </div>
    );
};

const COMPOSER_WORKI_SIZE = 64;

const ComposerThinkingDots = () => <ThinkingDots x={215} y={-40} dotSize={18} gap={13} cycle />;
const THINKING_ON_COMPOSER: Pose = { ...THINKING, accessories: [ComposerThinkingDots] };

const ComposerWorki = ({ isThinking = false }: { isThinking?: boolean }) => (
    <div
        className="pointer-events-none absolute right-9 z-20 select-none"
        style={{ top: COMPOSER_WORKI_SIZE * -0.87 + 1 }}
    >
        <Worki
            pose={isThinking ? THINKING_ON_COMPOSER : SIT}
            size={COMPOSER_WORKI_SIZE}
            trackMouse="page"
            showGlow={false}
            idle={false}
        />
    </div>
);

const ComposerStage = () => (
    <>
        <div
            className="composer-glow pointer-events-none absolute -z-10 opacity-60 transition-opacity duration-500 group-focus-within/composer:opacity-100"
            style={{ left: '-20%', width: '140%', top: '-110%', height: '320%' }}
        />
        <ComposerWorki />
    </>
);

const NewConversationView = ({ agents, projects, models, projectsLoading, onOpenSidebar }) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();
    const { limitReached: costLimitReached } = useChatUsage();
    const online = useOnlineStatus();

    // no project by default = workspace chat; the user picks one via the composer's + menu, or arrives with one
    // preselected from the projects page ("Use in chat")
    const [projectId, setProjectId] = useState<string | null>(
        () => (location.state as { projectId?: string })?.projectId || null
    );
    const [creating, setCreating] = useState(false);
    const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());

    // no projectId = workspace chat (ENG-10934): the backend derives the toolset from the user
    const { data: toolsData } = useQuery(GET_CHAT_TOOLS, {
        variables: { projectId },
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('new.loadError')), status: 'error' }),
    });
    const tools = useMemo(() => toolsData?.chatTools || [], [toolsData]);

    // remember each project's tool selection across conversations: { [projectId]: string[] }
    const [storedToolSelections, setStoredToolSelections] = useLocalStorage('chat.toolSelections', {});

    // when the project (and thus its tool list) changes, restore the saved selection — intersected with the
    // currently available tools — or fall back to everything except the context-heavy master data catalog
    useEffect(() => {
        if (!tools.length) return;
        const available = tools.map((tool) => tool.name);
        // selections saved before projects existed are stored under the agent's id
        const project = projects.find((candidate) => candidate.id === projectId);
        const saved = projectId
            ? (storedToolSelections[projectId] ?? (project?.agent ? storedToolSelections[project.agent.id] : undefined))
            : undefined;
        // the searchable article tool replaced the article number lookup; a selection saved with the old name
        // keeps article search
        const savedNames = saved?.includes('FindArticleNumberToolClass')
            ? [...saved, 'SearchArticlesToolClass']
            : saved;
        const next = savedNames
            ? available.filter((name) => savedNames.includes(name))
            : available.filter((name) => !/AccessArticleCatalog/i.test(name));
        setSelectedTools(new Set(next));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tools, projectId]);

    const toggleTool = (name: string) => {
        const next = new Set(selectedTools);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        setSelectedTools(next);
        if (projectId) setStoredToolSelections((prev) => ({ ...prev, [projectId]: [...next] }));
    };

    const [createConversation] = useMutation(CREATE_CONVERSATION);

    const [modelKey, setModelKey] = useState<string | null>(null);
    const noAgents = agents.length === 0;
    const terraModel = models.find((model) => /terra/i.test(model.key));

    useEffect(() => {
        if (modelKey === null && noAgents && terraModel) {
            setModelKey(terraModel.key);
        }
    }, [modelKey, noAgents, terraModel]);
    const selectedModel = models.find((model) => model.key === modelKey) || models[0];

    const [uploadAttachment] = useMutation(UPLOAD_CONVERSATION_ATTACHMENT);

    const handleSend = async (
        message: string,
        _attachmentIds: string[] = [],
        files: File[] = [],
        promptMetadata?: { promptId?: string; promptCategory?: string }
    ) => {
        if (costLimitReached) return;
        setCreating(true);
        try {
            const entryPoint = (location.state as { entryPoint?: string })?.entryPoint || CHAT_ENTRY_POINT.DIRECT_URL;
            const pendingPromptMeta = pendingPromptMetaRef.current;
            pendingPromptMetaRef.current = null;

            const verifiedPendingPromptMeta =
                pendingPromptMeta && promptTemplateStillMatches(pendingPromptMeta.promptText, message)
                    ? pendingPromptMeta
                    : undefined;

            const promptId = promptMetadata?.promptId ?? verifiedPendingPromptMeta?.promptId;
            const promptCategory = promptMetadata?.promptCategory ?? verifiedPendingPromptMeta?.promptCategory;
            const { data } = await createConversation({
                variables: {
                    projectId,
                    modelKey: selectedModel?.key,
                    toolsEnabled: true,
                    selectedTools: [...selectedTools],
                    entryPoint,
                    promptId,
                    promptCategory,
                },
            });
            const conversationId = data.createAgentConversation.conversation.id;

            const attachmentIds: string[] = [];
            for (const file of files) {
                const { data: uploadData } = await uploadAttachment({ variables: { conversationId, file } });
                attachmentIds.push(uploadData.uploadAgentConversationAttachment.attachment.id);
            }

            // navigate immediately; ThreadView picks up the pending message/attachments from router
            // state and sends the first turn itself via useConversationStream
            navigate(url(CONVERSATION_PATH, { conversationId }), {
                state: { pendingMessage: message, pendingAttachmentIds: attachmentIds },
            });
        } catch (e) {
            console.error(e);
            publishToast({ description: graphqlErrorMessage(e, t('thread.sendError')), status: 'error' });
            setCreating(false);
        }
    };

    const { data: channelsData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'cache-first',
        skip: !canSeeDocuments(user), // the backend denies the channel lists without the documents permission
    });
    const hasNoChannels = channelsData !== undefined && CHANNEL_FIELDS.every((field) => !channelsData[field]?.length);
    const documentType = resolveSingleDocumentType(channelsData, CHANNEL_FIELDS);
    const promptCategories = t('promptLibrary.categories', { returnObjects: true }) as PromptCategoryTranslations;
    const promptLibrary = resolveFeaturedPrompts(promptCategories, documentType).filter(
        ({ requiresAttachment }) => !(requiresAttachment && hasNoChannels)
    );
    const composerRef = useRef<ComposerHandle>(null);
    const pendingPromptMetaRef = useRef<{ promptId: string; promptCategory: string; promptText: string } | null>(null);

    const handlePromptClick = (
        prompt: string,
        promptId: string,
        category: PromptCategoryKey,
        requiresAttachment?: boolean
    ) => {
        const requiresInput = promptRequiresInput(prompt, { requiresAttachment });

        if (requiresInput) {
            pendingPromptMetaRef.current = { promptId, promptCategory: category, promptText: prompt };
            composerRef.current?.fillPrompt(prompt);
        } else {
            handleSend(prompt, [], [], { promptId, promptCategory: category });
        }
    };

    // a prompt chosen on PromptLibraryPage (/chat/prompts) is handed off here via router state,
    // the same way ThreadView picks up a pending first message — this view doesn't exist yet
    // when the user is on the library page, so it can't be filled/sent directly from there.
    // Composer only mounts once the agents-loading Loader below has cleared, so this effect must
    // wait for that too (re-running on agentsLoading) rather than firing once on mount, or
    // composerRef.current would still be null the first time it runs.
    const initialPromptRef = useRef(false);
    useEffect(() => {
        if (projectsLoading && !projects.length) return;
        const { fillPrompt, sendPrompt, promptId, promptCategory } =
            (location.state as {
                fillPrompt?: string;
                sendPrompt?: string;
                promptId?: string;
                promptCategory?: string;
            }) || {};
        if ((!fillPrompt && !sendPrompt) || initialPromptRef.current) return;
        initialPromptRef.current = true;
        // clear the handoff state immediately so a refresh doesn't replay it
        navigate(CHAT_PATH, { replace: true });
        if (fillPrompt) {
            if (promptId && promptCategory) {
                pendingPromptMetaRef.current = { promptId, promptCategory, promptText: fillPrompt };
            }
            composerRef.current?.fillPrompt(fillPrompt);
        } else if (sendPrompt) {
            handleSend(sendPrompt, [], [], promptId ? { promptId, promptCategory } : undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectsLoading]);

    // no projects = workspace chat (ENG-10934): the new-conversation view works without one
    if (projectsLoading && !projects.length) return <Loader />;

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex h-14 flex-none items-center gap-3 border-0 border-b border-solid border-line-weak bg-surface-weak px-5">
                <Page.HeaderNavigationToggle />
                <Page.HeaderTitle>{t('new.title')}</Page.HeaderTitle>

                <div className="ml-auto flex items-center gap-3">
                    <SidebarToggle onClick={onOpenSidebar} />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="relative flex h-full flex-col items-center justify-center overflow-hidden px-6 pb-2 pt-10 [container-type:inline-size]">
                    <div className="relative z-10 flex w-full flex-col items-center">
                        <h2 className="m-0 mb-1.5 text-xl font-semibold text-content-strong">
                            {t('new.greeting', { name: user?.firstName })}
                        </h2>

                        <div className="w-full mt-10 mb-5">
                            <MonthlyBudgetWarning className="mb-3" />
                            {costLimitReached && (
                                <div className="mx-auto mb-3 w-full max-w-3xl px-6">
                                    <CostLimitNotice />
                                </div>
                            )}
                            {!online && (
                                <div className="mx-auto mb-3 w-full max-w-3xl px-6">
                                    <OfflineNotice />
                                </div>
                            )}
                            <Composer
                                ref={composerRef}
                                onSend={handleSend}
                                disabled={creating || costLimitReached}
                                sendDisabled={!online}
                                placeholder={t('composer.newPlaceholder')}
                                tools={tools}
                                selectedTools={selectedTools}
                                onToggleTool={toggleTool}
                                project={projectId}
                                projects={projects}
                                onSelectProject={setProjectId}
                                onCreateProject={() => navigate(PROJECTS_PATH, { state: { createProject: true } })}
                                models={models}
                                selectedModelKey={selectedModel?.key}
                                onModelChange={setModelKey}
                                decoration={<ComposerStage />}
                            />

                            <Footnote />
                        </div>
                        <div className="w-full max-w-3xl rounded-2xl py-3 px-8.5">
                            {promptLibrary.map(({ text, promptId, category, icon: PromptIcon, requiresAttachment }) => (
                                <button
                                    key={text}
                                    disabled={costLimitReached}
                                    className="flex cursor-pointer mb-1.5 items-center gap-3 rounded-lg border-none bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-surface-brand-weak-default/50 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                                    onClick={() => handlePromptClick(text, promptId, category, requiresAttachment)}
                                >
                                    <PromptIcon className="flex-none text-content-brand-default w-4" />
                                    <span className="text-sm font-medium text-content-strong">
                                        {renderPromptLibraryText(text)}
                                    </span>
                                </button>
                            ))}
                            <Link
                                to={PROMPT_LIBRARY_PATH}
                                className="flex items-center gap-3 mb-1.5 rounded-lg px-3 py-1.5 text-left transition-colors hover:bg-surface-brand-weak-default/50"
                            >
                                <MoreIcon className="flex-none text-content-strong w-4" />
                                <span className="text-sm font-medium text-content-strong">
                                    {t('promptLibrary.more')}
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ChatPage = () => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const { conversationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useApplicationContext();
    // account-wide admin opt-out (ENG-10895): the page is reachable, but shows the teaser as for a new user
    const chatDisabledByAdmin = user?.customer?.agentChatEnabled === false;
    const chatEnabled = canUseChat(user);

    const [automationDialog, setAutomationDialog] = useState<{
        mode: AutomationDialogMode;
        schedule?: any;
        trigger?: any;
        conversationId?: string;
        initialPrompt?: string;
        initialForm?: Partial<ScheduleFormState>;
        // the chat proposal this dialog was opened from: once the dialog saves, the pending tool call is
        // confirmed with the saved schedule's id, so the thread shows the same created-schedule card as
        // an in-chat confirmation (closing without saving falls back to the proposal card)
        supersedesMessageId?: string;
        // remounts the dialog when it is reopened for something else
        key: string;
    } | null>(null);
    const [resolveSupersededToolCall] = useMutation(CONFIRM_AGENT_TOOL_CALL);

    // on small screens the conversation sidebar opens as a drawer from the header; ChatPage renders the Layout
    // itself, so it cannot read the breakpoint from the layout context
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
    const [historyOpen, setHistoryOpen] = useState(false);

    // the drawer covers the page, so it closes whenever what it covers changes (navigation, a panel opening)
    useEffect(() => {
        setHistoryOpen(false);
    }, [location.pathname, conversationId, automationDialog]);

    useEscapeKey(isMobile && historyOpen, () => setHistoryOpen(false));

    const { data: conversationsData } = useQuery(GET_CONVERSATIONS, {
        skip: !chatEnabled,
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('sidebar.loadError')), status: 'error' }),
    });
    // the run toasts poll this query, which keeps this copy (sidebar, chat header) fresh as well
    const { data: schedulesData } = useQuery(GET_CONVERSATION_SCHEDULES, { skip: !chatEnabled });
    // users without an enabled agent run their email automations as themselves; only agent managers may put an
    // automation on an agent
    const { hasNoEnabledAgent, loading: agentCheckLoading } = useHasNoEnabledAgent();
    // email automations, for the sidebar's Automations warning
    const { data: myTriggersData } = useQuery(GET_MY_TRIGGERS, { skip: !chatEnabled });
    const { data: agentsData } = useQuery(GET_CHAT_AGENTS, {
        skip: !chatEnabled,
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('new.loadError')), status: 'error' }),
    });
    // the chat picker lists projects (ENG-11385): agent-backed ones stand in for the agents it used to list
    const { data: projectsData, loading: projectsLoading } = useQuery(GET_PROJECTS, {
        skip: !chatEnabled,
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('projects.loadError')), status: 'error' }),
    });
    // like the agent picker before it, the chat offers plain projects and those whose inbox agent is enabled
    const projects = (projectsData?.projects || []).filter((project) => !project.agent || project.agent.enabled);
    // snapshots other people shared with the viewer, for the sidebar (ENG-11386); nothing the viewer does changes
    // this list, so it polls and refreshes when the tab comes back
    const { data: sharedData, refetch: refetchShared } = useQuery(GET_SHARED_CONVERSATIONS, {
        skip: !chatEnabled,
        pollInterval: SHARED_CONVERSATIONS_UPDATE_INTERVAL,
        skipPollAttempt: skipPollAttemptWhenHidden,
    });
    useRefetchOnTabVisible(refetchShared, chatEnabled);

    const projectsMatch = useMatch(PROJECTS_PATH);
    const projectMatch = useMatch(PROJECT_PATH);
    const projectsView = Boolean(projectsMatch || projectMatch);
    const automationsMatch = useMatch(AUTOMATIONS_PATH);
    const automationMatch = useMatch(AUTOMATION_PATH);
    const automationsView = Boolean(automationsMatch || automationMatch);
    const { data: modelsData } = useQuery(GET_AVAILABLE_CHAT_MODELS, {
        skip: !chatEnabled,
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('new.loadError')), status: 'error' }),
    });

    const openScheduleEdit = (schedule: any) => {
        setAutomationDialog({ mode: 'edit', schedule, key: `edit-${schedule.id}` });
    };

    useScheduleRunToasts({ enabled: chatEnabled });

    const editScheduleId = automationMatch?.params.scheduleId;
    const editRequested =
        (location.state as typeof EDIT_SCHEDULE_STATE | null)?.editSchedule === EDIT_SCHEDULE_STATE.editSchedule;
    useEffect(() => {
        if (!editRequested || !editScheduleId || !schedulesData) return;
        const schedule = schedulesData.agentConversationSchedules?.find((item) => item.id === editScheduleId);
        if (schedule) openScheduleEdit(schedule);
        navigate(location.pathname, { replace: true, state: {} });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editRequested, editScheduleId, schedulesData]);

    if (!chatEnabled) {
        return (
            <Layout>
                <div className="flex h-full min-w-0 flex-1 bg-surface-medium">
                    <WorkiTeaserPage disabledByAdmin={chatDisabledByAdmin} />
                </div>
            </Layout>
        );
    }

    const conversations = conversationsData?.agentConversations || [];
    const schedules = schedulesData?.agentConversationSchedules || [];
    const agents = (agentsData?.agents || []).filter((agent) => agent.enabled);
    const models = modelsData?.availableChatModels || [];
    const modelLabels = Object.fromEntries(models.map((model) => [model.key, model.label]));
    const myTriggers = myTriggersData?.myTriggers || [];
    const canUseAgentTriggers = !agentCheckLoading && !hasNoEnabledAgent && canManageAgents(user);

    const openScheduleDialog = ({
        schedule,
        conversationId: targetId,
        prompt,
        messageId,
    }: {
        schedule?: any;
        conversationId?: string;
        prompt?: string;
        messageId?: string;
    } = {}) => {
        if (schedule) {
            openScheduleEdit(schedule);
        } else {
            setAutomationDialog({
                mode: 'fromMessage',
                conversationId: targetId ?? conversationId,
                initialPrompt: prompt,
                key: `message-${messageId ?? 'blank'}`,
            });
        }
    };

    const openEmailAutomationEdit = (trigger: any) => {
        setAutomationDialog({ mode: 'editEmail', trigger, key: `edit-email-${trigger.id}` });
    };

    const openNewAutomation = () => setAutomationDialog({ mode: 'new', key: 'new' });

    const automationsWarning =
        schedules.some((schedule) => schedule.autoPaused || schedule.lastRunStatus === 'failed') ||
        myTriggers.some((trigger) => trigger.lastStatus === 'failed');
    const scheduledConversationIds = new Set<string>(schedules.map((schedule) => schedule.conversationId));

    return (
        <Layout>
            <div className="relative flex h-full min-w-0 flex-1 bg-surface-medium">
                {historyOpen && (
                    <div
                        className="absolute inset-0 z-40 bg-[rgba(0,0,0,0.5)] touch-none md:hidden"
                        onClick={() => setHistoryOpen(false)}
                    />
                )}
                <div
                    inert={isMobile && !historyOpen}
                    className={classnames(
                        'flex flex-none max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-72 max-md:shadow-xl max-md:transition-transform max-md:duration-200',
                        !historyOpen && 'max-md:-translate-x-full'
                    )}
                >
                    <ConversationSidebar
                        conversations={conversations}
                        sharedConversations={sharedData?.sharedAgentConversations || []}
                        activeId={conversationId}
                        scheduledConversationIds={scheduledConversationIds}
                        schedules={schedules}
                        triggers={myTriggers}
                        onOpenTrigger={openEmailAutomationEdit}
                        canStartConversation
                        onOpenProjects={() => navigate(PROJECTS_PATH)}
                        projectsActive={projectsView}
                        onOpenAutomations={() => navigate(AUTOMATIONS_PATH)}
                        automationsActive={automationsView}
                        automationsWarning={automationsWarning}
                        onClose={isMobile ? () => setHistoryOpen(false) : null}
                    />
                </div>
                {projectsView ? (
                    <ProjectsPage
                        projectId={projectMatch?.params.projectId}
                        section={projectMatch?.params.section}
                        onOpenSidebar={() => setHistoryOpen(true)}
                    />
                ) : automationsView ? (
                    <AutomationsPage
                        scheduleId={automationMatch?.params.scheduleId}
                        onOpenSidebar={() => setHistoryOpen(true)}
                        onNewAutomation={openNewAutomation}
                        onEditSchedule={(schedule) => openScheduleDialog({ schedule })}
                        canEditTriggers
                        onEditTrigger={openEmailAutomationEdit}
                    />
                ) : conversationId ? (
                    <ThreadView
                        key={conversationId}
                        conversationId={conversationId}
                        // the first of the chat's schedules, for the header's schedule mark
                        schedule={schedules.find((schedule) => schedule.conversationId === conversationId) ?? null}
                        modelLabels={modelLabels}
                        onSchedule={(message) =>
                            openScheduleDialog({ conversationId, prompt: message.content, messageId: message.id })
                        }
                        // "Adjust details" on a chat schedule proposal: the full dialog, prefilled with the
                        // proposed (and possibly already adjusted) settings; the proposal card stays as the
                        // fallback until the dialog saves
                        onAdjustSchedule={(args, messageId) =>
                            setAutomationDialog({
                                mode: 'fromMessage',
                                conversationId,
                                initialPrompt: args.prompt,
                                initialForm: formFromToolArgs(args),
                                supersedesMessageId: messageId,
                                key: `adjust-${messageId}`,
                            })
                        }
                        onEditSchedule={openScheduleEdit}
                        onOpenSidebar={() => setHistoryOpen(true)}
                    />
                ) : (
                    <NewConversationView
                        agents={agents}
                        projects={projects}
                        models={models}
                        projectsLoading={projectsLoading}
                        onOpenSidebar={() => setHistoryOpen(true)}
                    />
                )}
                {automationDialog && (
                    <AutomationDialog
                        key={automationDialog.key}
                        mode={automationDialog.mode}
                        schedule={automationDialog.schedule}
                        trigger={automationDialog.trigger}
                        conversationId={automationDialog.conversationId}
                        initialPrompt={automationDialog.initialPrompt}
                        initialForm={automationDialog.initialForm}
                        onSaved={
                            automationDialog.supersedesMessageId
                                ? (saved) =>
                                      resolveSupersededToolCall({
                                          variables: {
                                              messageId: automationDialog.supersedesMessageId,
                                              payload: JSON.stringify({ created_schedule_id: saved.id }),
                                          },
                                      }).catch(() => null)
                                : undefined
                        }
                        onClose={() => setAutomationDialog(null)}
                        canUseEmail
                        agents={canUseAgentTriggers ? agents : []}
                        projects={projects}
                        initialAgentId={
                            canUseAgentTriggers
                                ? conversations.find((c) => c.id === conversationId)?.project?.agent?.id
                                : null
                        }
                    />
                )}
            </div>
        </Layout>
    );
};

export default ChatPage;
